import {
  Analytics,
  logEvent,
  setCurrentScreen,
  setDefaultEventParameters,
  setUserId,
  setUserProperties,
} from "firebase/analytics"

import { CLASS_GROUPS } from "#constants/classOptions"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { EnumValues } from "#customTypes/types"

import Firebase from "./Firebase"

// Analytics event constants
export const ANALYTICS_EVENTS = {
  // User actions
  USER_LOGIN: "User Login",
  USER_LOGOUT: "User Logout",
  USER_SIGNUP: "User Signup",

  // Class management
  CLASS_CREATED: "Class Created",
  CLASS_UPDATED: "Class Updated",
  CLASS_DELETED: "Class Deleted",
  CLASS_VIEWED: "Class Viewed",

  // Student management
  STUDENT_ADDED: "Student Added",
  STUDENT_UPDATED: "Student Updated",
  STUDENT_DELETED: "Student Deleted",
  STUDENT_VIEWED: "Student Viewed",

  STUDENT_ADDED_TO_CLASS: "Student Added to Class",
  STUDENT_REMOVED_FROM_CLASS: "Student Removed from Class",
  STUDENT_ENROLLMENT_UPDATED: "Student Enrollment Updated",

  TEACHER_ADDED_TO_CLASS: "Teacher Added to Class",
  TEACHER_REMOVED_FROM_CLASS: "Teacher Removed from Class",
  TEACHER_ENROLLMENT_UPDATED: "Teacher Enrollment Updated",

  ASSISTANT_ADDED_TO_CLASS: "Assistant Added to Class",
  ASSISTANT_REMOVED_FROM_CLASS: "Assistant Removed from Class",
  ASSISTANT_ENROLLMENT_UPDATED: "Assistant Enrollment Updated",

  // Attendance
  ATTENDANCE_UPDATED: "Attendance Updated",

  // Evaluations
  EVALUATION_STARTED: "Evaluation Started",
  EVALUATION_COMPLETED: "Evaluation Completed",
  EVALUATION_SAVED: "Evaluation Saved",

  // GPT Feedback
  GPT_FEEDBACK_STARTED: "GPT Feedback Started",
  GPT_FEEDBACK_GENERATED: "GPT Feedback Generated",
  GPT_FEEDBACK_ACCEPTED: "GPT Feedback Accepted",

  // Standout Moments
  STANDOUT_MOMENT_SAVED: "Standout Moment Saved",
  STANDOUT_FEEDBACK_GENERATED: "Standout Feedback Generated",
  STANDOUT_FEEDBACK_REAPPLIED: "Standout Feedback Reapplied",

  // Artwork crop / rotate / zoom. `source` says which surface opened it.
  ARTWORK_IMAGE_EDITED: "Artwork Image Edited",

  // Curriculum management
  CURRICULUM_CREATED: "Curriculum Created",
  CURRICULUM_UPDATED: "Curriculum Updated",
  CURRICULUM_DELETED: "Curriculum Deleted",

  // Makeups
  MAKEUP_SCHEDULED: "Makeup Scheduled",
  MAKEUP_CANCELLED: "Makeup Cancelled",

  // Tryouts
  TRYOUT_SCHEDULED: "Tryout Scheduled",
  TRYOUT_CANCELLED: "Tryout Cancelled",

  // Projects
  PROJECT_CREATED: "Project Created",
  PROJECT_UPDATED: "Project Updated",
  PROJECT_DELETED: "Project Deleted",

  // Navigation
  PAGE_VIEW: "Page View",
  MODAL_OPENED: "Modal Opened",
  MODAL_CLOSED: "Modal Closed",

  // Errors
  ERROR_OCCURRED: "Error Occurred",

  // Performance
  LOAD_TIME: "Load Time",
  API_CALL: "API Call",

  // Help Requests
  HELP_REQUEST_CREATED: "Help Request Created",
  HELP_REQUEST_ACKNOWLEDGED: "Help Request Acknowledged",
  HELP_REQUEST_RESOLVED: "Help Request Resolved",

  // Impersonation
  IMPERSONATION_STARTED: "Impersonation Started",
  IMPERSONATION_STOPPED: "Impersonation Stopped",
} as const

export type AnalyticsEventKey = keyof typeof ANALYTICS_EVENTS
export type AnalyticsEvent = EnumValues<typeof ANALYTICS_EVENTS>

// Cached app version - fetched once and reused
let cachedAppVersion: string | null = null

export const getAppVersion = async (): Promise<string> => {
  if (cachedAppVersion) return cachedAppVersion

  try {
    const versionDocs = await Firebase.getInstance().variableQuery<{
      version: string
      id: string
    }>(DOC_TYPES.VERSIONS, {
      queries: {},
      orderBy: { field: "version", direction: "desc" },
      limitCount: 1,
    })
    cachedAppVersion = versionDocs[0]?.version ?? "unknown"
  } catch (error) {
    console.error("Failed to fetch app version:", error)
    cachedAppVersion = "unknown"
  }

  return cachedAppVersion
}

class FirebaseAnalyticsSingleton {
  private readonly analytics: Analytics | null
  private isInitialized = false

  constructor(analytics: Analytics | null) {
    this.analytics = analytics
    this.initialize()
  }

  private initialize() {
    if (this.isInitialized) return
    this.isInitialized = true

    // Defer to avoid circular dependency - Firebase may still be constructing
    queueMicrotask(() => {
      void getAppVersion().then((appVersion) => {
        setDefaultEventParameters({
          appVersion,
          platform: "web",
        })
      })
    })
  }

  // Core tracking methods
  trackEvent = (
    eventName: AnalyticsEvent,
    parameters?: Record<string, unknown>
  ) => {
    if (!this.analytics) {
      console.warn("Analytics not initialized")
      return
    }

    try {
      // Track with Firebase Analytics
      logEvent(this.analytics, eventName, {
        timestamp: Date.now(),
        ...parameters,
      })

      // Also save to Firestore for backup/analysis
      this.saveEventToFirestore(eventName, parameters)
    } catch (error) {
      console.error("Analytics tracking error:", error)
    }
  }

  // Save event to Firestore as a document
  private saveEventToFirestore = async (
    eventName: AnalyticsEvent,
    parameters?: Record<string, unknown>
  ) => {
    const eventData = {
      eventName,
      timestamp: Date.now(),
      createdAt: new Date().toISOString(),
      ...parameters,
    }

    // Console log during development on localhost
    if (window.location.hostname === "localhost") {
      console.log("📊 Analytics Event (localhost):", eventData)
      return
    }

    try {
      await Firebase.getInstance().addDocument(DOC_TYPES.ANALYTICS, eventData)
    } catch (error) {
      console.error("Failed to save analytics event to Firestore:", error)
    }
  }

  // User identification
  setUserId = (userId: string) => {
    if (!this.analytics) return

    try {
      setUserId(this.analytics, userId)
    } catch (error) {
      console.error("Error setting user ID:", error)
    }
  }

  setUserProperties = (properties: Record<string, unknown>) => {
    if (!this.analytics) return

    try {
      setUserProperties(this.analytics, properties)
    } catch (error) {
      console.error("Error setting user properties:", error)
    }
  }

  // Screen tracking
  setCurrentScreen = (screenName: string, screenClass?: string) => {
    if (!this.analytics) return

    try {
      setCurrentScreen(this.analytics, screenName, screenClass)
      this.trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, { screenName })
    } catch (error) {
      console.error("Error setting current screen:", error)
    }
  }

  // Convenience methods for common events
  trackUserLogin = (method: "email" | "google") => {
    this.trackEvent(ANALYTICS_EVENTS.USER_LOGIN, { method })
  }

  trackUserLogout = () => {
    this.trackEvent(ANALYTICS_EVENTS.USER_LOGOUT)
  }

  trackClassAction = (
    action: "created" | "updated" | "deleted" | "viewed",
    classId: Uuid,
    className: string,
    userId?: string
  ) => {
    const eventMap = {
      created: ANALYTICS_EVENTS.CLASS_CREATED,
      updated: ANALYTICS_EVENTS.CLASS_UPDATED,
      deleted: ANALYTICS_EVENTS.CLASS_DELETED,
      viewed: ANALYTICS_EVENTS.CLASS_VIEWED,
    }
    this.trackEvent(eventMap[action], {
      classId,
      className,
      userId,
    })
  }

  trackStudentAction = (
    action: "added" | "updated" | "deleted" | "viewed",
    studentId: string,
    studentName?: string,
    userId?: string
  ) => {
    const eventMap = {
      added: ANALYTICS_EVENTS.STUDENT_ADDED,
      updated: ANALYTICS_EVENTS.STUDENT_UPDATED,
      deleted: ANALYTICS_EVENTS.STUDENT_DELETED,
      viewed: ANALYTICS_EVENTS.STUDENT_VIEWED,
    }
    this.trackEvent(eventMap[action], {
      studentId,
      studentName,
      userId,
    })
  }

  trackAttendanceAction = (
    action: typeof ANALYTICS_EVENTS.ATTENDANCE_UPDATED,
    studentId: string,
    classId: string,
    sessionId: string,
    status: string,
    userId?: string
  ) => {
    this.trackEvent(action, {
      studentId,
      classId,
      sessionId,
      status,
      userId,
    })
  }

  trackEvaluationAction = (
    action:
      | typeof ANALYTICS_EVENTS.EVALUATION_STARTED
      | typeof ANALYTICS_EVENTS.EVALUATION_COMPLETED
      | typeof ANALYTICS_EVENTS.EVALUATION_SAVED,
    parameters: {
      studentId: Uuid
      classId: Uuid
      classGroup: CLASS_GROUPS
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackError = (error: Error, context?: string, userId?: string) => {
    this.trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      errorMessage: error.message,
      errorStack: error.stack,
      context,
      userId,
    })
  }

  trackLoadTime = (page: string, loadTime: number, userId?: string) => {
    this.trackEvent(ANALYTICS_EVENTS.LOAD_TIME, {
      page,
      loadTimeMs: loadTime,
      userId,
    })
  }

  trackApiCall = (
    endpoint: string,
    method: string,
    statusCode: number,
    duration?: number,
    userId?: string
  ) => {
    this.trackEvent(ANALYTICS_EVENTS.API_CALL, {
      endpoint,
      method,
      statusCode,
      durationMs: duration,
      userId,
    })
  }
}

export default class FirebaseAnalytics {
  private static instance: FirebaseAnalyticsSingleton | null = null

  constructor(analytics: Analytics | null) {
    if (FirebaseAnalytics.instance == null) {
      FirebaseAnalytics.instance = new FirebaseAnalyticsSingleton(analytics)
    }
  }

  public static getInstance(): FirebaseAnalyticsSingleton {
    if (FirebaseAnalytics.instance == null) {
      FirebaseAnalytics.instance = new FirebaseAnalyticsSingleton(
        Firebase.getInstance().analytics
      )
    }
    return FirebaseAnalytics.instance
  }
}
